<template>
    <BaseCard>
        <div class="simple-card">
            <div class="simple-card-header">
                <!-- The title is a static string, so it stays visible while loading:
                     replacing it with a bar would collapse a wrapped two-line title
                     to one line and change the card height. -->
                <p class="simple-card-heading simple-card-heading--sub">{{ title }}</p>
                <h1
                    class="simple-card-heading"
                    :class="color"
                    :data-value="value"
                    :style="{ fontSize: font_size + 'rem' }"
                    data-cy="card-header-value"
                >
                    <span v-if="loading" class="skeleton-bar skeleton-bar--value loader" />
                    <span v-else-if="withoutFormatCell">{{ prefix }}{{ value }}{{ postfix }}</span>
                    <span v-else>
                        {{ prefix }}{{ formatCell(value, isDecimal, false, color === "grand_total") }}{{ postfix }}
                    </span>
                </h1>
            </div>
            <!-- While loading the caller can't know yet whether a second row will
                 have content, so reserve it whenever the prop is bound at all —
                 that keeps the skeleton exactly as tall as the loaded card. -->
            <div v-if="title_2 || (loading && title_2 !== undefined)" class="simple-card-header">
                <p class="simple-card-heading simple-card-heading--sub">
                    <span v-if="loading" class="skeleton-bar loader" />
                    <template v-else>{{ title_2 }}</template>
                </p>
                <h1
                    class="simple-card-heading"
                    :class="color"
                    :data-value="value"
                    :style="{ fontSize: font_size + 'rem' }"
                    data-cy="card-header-value"
                >
                    <!-- Only placeholder a second value when the caller actually
                         supplies one — otherwise this heading renders empty once
                         loaded and a bar here would make the card too tall. -->
                    <template v-if="loading">
                        <span v-if="value_2 !== undefined" class="skeleton-bar skeleton-bar--value loader" />
                    </template>
                    <span v-else-if="withoutFormatCell"> {{ prefix }}{{ value_2 }}{{ postfix }} </span>
                    <span v-else> {{ prefix }}{{ formatCell(value_2, isDecimal) }}{{ postfix }} </span>
                </h1>
            </div>
            <div class="simple-card-content">
                <slot name="default" />
            </div>
            <div class="simple-card-footer" v-if="!!$slots.footer">
                <slot name="footer" />
            </div>
        </div>
    </BaseCard>
</template>

<script>
import BaseCard from "@/components/BaseCard";
import { formatCell } from "@/helpers/filters";

export default {
    name: "SimpleCard",
    components: {
        BaseCard,
    },
    props: {
        title: {
            type: String,
            required: true,
        },
        value: {
            type: [String, Number],
            required: true,
        },
        title_2: {
            type: String,
            required: false,
        },
        value_2: {
            type: [String, Number],
            required: false,
        },
        prefix: {
            type: String,
            required: false,
            default: "",
        },
        postfix: {
            type: String,
            required: false,
            default: "",
        },
        isDecimal: {
            type: Boolean,
            required: false,
            default: false,
        },
        withoutFormatCell: {
            type: Boolean,
            required: false,
            default: false,
        },
        color: {
            type: String,
            required: false,
            default: "",
            validator: (value) => {
                return !value ? true : ["success", "warning", "grand_total"].indexOf(value) !== -1;
            },
        },
        font_size: {
            type: Number,
            required: false,
            default: 3,
        },
        loading: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    setup() {
        return { formatCell };
    },
};
</script>

<style lang="scss" scoped>
.simple-card {
    display: flex;
    flex-direction: column;
    height: 100%;

    &-content {
        margin-top: auto;
    }

    &-footer {
        border-top: 1px solid $dark-grey;
        padding-top: 1.5rem;
        margin-top: 1.5rem;
    }
}

// Placeholder bars are sized in `em` so they inherit each heading's font size,
// and kept inside the text line box (0.7em, vertically centred) so a loading
// card is pixel-identical in height to the loaded one.
.skeleton-bar {
    display: inline-block;
    vertical-align: middle;
    width: 60%;
    height: 0.7em;
    background: $grey;
    border-radius: $normal-radius;

    &--value {
        width: 45%;
    }
}

.simple-card-heading {
    font-size: 3rem;
    text-align: center;
    color: $blue;
    margin: 0.8rem 0;
    padding: 0;
    font-weight: 400;
    text-transform: none;
    @include --big-mobile-viewport {
        font-size: 2rem;
    }

    &--sub {
        font-size: 1.2rem;
        font-weight: 400;
        @include --big-mobile-viewport {
            font-size: 1rem;
        }
    }

    &.success {
        color: $green;
    }

    &.warning {
        color: $red;
    }

    &.grand_total {
        color: $green;
    }
}

body.dark {
    .simple-card-heading {
        color: $text;

        &.success {
            //background: $green-gradient;
            background: $green-2;
            background-clip: text;
            -webkit-text-fill-color: transparent;
        }

        &.grand_total {
            color: $lime;
        }

        &.warning {
            background: $red-gradient;
            background-clip: text;
            -webkit-text-fill-color: transparent;
        }
    }
}

body.variant-performax {
    .simple-card-heading {
        color: $px-purple;

        &.success {
            background: $green-2;
            background-clip: text;
            -webkit-text-fill-color: transparent;
        }

        &.warning {
            background: $red-gradient;
            background-clip: text;
            -webkit-text-fill-color: transparent;
        }

        &.grand_total {
            color: $lime;
        }
    }
}
</style>
